/**
 * DataTable — sdílená tabulka pro ACM Dashboard.
 * Konzistentní hlavičky, hover, responsive.
 *
 * Řazení a hledání jsou volitelné (bez nich se tabulka chová jako dřív):
 *
 *   // řazení v prohlížeči — sloupec s `sortable`, hodnota z row[key] nebo sortValue
 *   <DataTable columns={[{ key: 'pages', label: 'Stran', sortable: true }]}
 *     defaultSort={{ key: 'pages', dir: 'desc' }} searchable … />
 *
 *   // řazení / hledání na serveru — řízený režim: tabulka data sama neřadí ani nefiltruje
 *   <DataTable sort={sort} onSortChange={setSort} searchable search={q} onSearchChange={setQ} … />
 *
 * Čistá logika (řazení, hledání) je v data-table-logic.ts.
 */
import { cn } from '../../lib/utils';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { SearchInput } from './SearchInput';
import {
  ariaSort,
  filterRows,
  nextSort,
  rowText,
  sortRows,
  type SortState,
} from './data-table-logic';

export type { SortDir, SortState } from './data-table-logic';

export interface Column<T> {
  key: string;
  label: string;
  /** Bohatší obsah hlavičky (např. ikona + název). Bez něj se ukáže label. */
  header?: ReactNode;
  className?: string;
  render?: (row: T, index: number) => ReactNode;
  /** Sloupec jde řadit kliknutím na hlavičku. */
  sortable?: boolean;
  /** Hodnota pro řazení (výchozí row[key]) — např. když render ukazuje přeložený text. */
  sortValue?: (row: T) => unknown;
  /** Text sloupce pro vestavěné hledání (výchozí row[key], je-li text nebo číslo). */
  searchValue?: (row: T) => string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  className?: string;
  compact?: boolean;
  /** Hlavička zůstane nahoře, když tabulka roluje ve vlastním kontejneru (className s max-h + overflow-auto). */
  stickyHeader?: boolean;
  /** Výchozí řazení (neřízený režim, řadí se v prohlížeči). */
  defaultSort?: SortState;
  /** Řízené řazení: tabulka data neřadí, jen ukazuje stav a hlásí kliknutí (řazení na serveru). */
  sort?: SortState | null;
  onSortChange?: (sort: SortState) => void;
  /** Pole pro hledání nad tabulkou (sdílený SearchInput). */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Text řádku pro hledání; bez něj se skládá ze sloupců (searchValue / row[key]). */
  searchText?: (row: T) => string;
  /** Řízené hledání: tabulka nefiltruje, jen předá text (hledání na serveru). */
  search?: string;
  onSearchChange?: (search: string) => void;
}

export function DataTable<T extends Record<string, unknown>>({
  columns,
  data,
  rowKey,
  onRowClick,
  emptyMessage,
  className,
  compact,
  stickyHeader,
  defaultSort,
  sort: sortProp,
  onSortChange,
  searchable,
  searchPlaceholder,
  searchText,
  search: searchProp,
  onSearchChange,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const [localSort, setLocalSort] = useState<SortState | null>(defaultSort ?? null);
  const [localSearch, setLocalSearch] = useState('');
  const sortControlled = sortProp !== undefined;
  const searchControlled = searchProp !== undefined;
  const sort = sortControlled ? sortProp : localSort;
  const search = searchControlled ? searchProp : localSearch;

  const rows = useMemo(() => {
    let out = data;
    if (searchable && !searchControlled && search.trim()) {
      const text = searchText ?? ((row: T) =>
        columns
          .map((col) => col.searchValue ? col.searchValue(row) : rowText(row, [col.key]))
          .join(' '));
      out = filterRows(out, search, text);
    }
    if (!sortControlled && sort) {
      const col = columns.find((c) => c.key === sort.key && c.sortable);
      if (col) out = sortRows(out, sort, col.sortValue ?? ((row: T) => row[col.key]));
    }
    return out;
  }, [data, columns, searchable, searchControlled, search, searchText, sortControlled, sort]);

  const changeSort = (key: string) => {
    const next = nextSort(sort, key);
    if (!sortControlled) setLocalSort(next);
    onSortChange?.(next);
  };
  const changeSearch = (value: string) => {
    if (!searchControlled) setLocalSearch(value);
    onSearchChange?.(value);
  };

  const searchBox = searchable && (
    <SearchInput
      size='md'
      className='w-full sm:w-72'
      value={search}
      onChange={changeSearch}
      placeholder={searchPlaceholder ?? t('common.search')}
      {...(!searchControlled && search.trim()
        ? { resultCount: rows.length, totalCount: data.length }
        : {})}
    />
  );

  if (rows.length === 0) {
    const empty = (
      <div className='text-center py-12 text-gray-500 text-sm'>
        {search.trim() && data.length > 0 ? t('ui.noMatches') : emptyMessage ?? t('ui.noData')}
      </div>
    );
    if (!searchable) return empty;
    return (
      <div className='space-y-3'>
        {searchBox}
        {empty}
      </div>
    );
  }

  const table = (
    <div
      className={cn('rounded-xl border border-gray-800 overflow-hidden bg-gray-900/20', className)}
    >
      <table className='w-full text-left text-sm'>
        <thead
          className={cn(
            'bg-gray-900/40 border-b border-gray-800',
            stickyHeader && 'sticky top-0 z-10 bg-gray-950',
          )}
        >
          <tr>
            {columns.map((col) => {
              const state = col.sortable ? ariaSort(sort, col.key) : undefined;
              const SortIcon = state === 'ascending'
                ? ArrowUp
                : state === 'descending'
                ? ArrowDown
                : ArrowUpDown;
              return (
                <th
                  key={col.key}
                  aria-sort={state}
                  className={cn(
                    'text-xs font-semibold text-gray-500 uppercase tracking-wider',
                    compact ? 'px-3 py-2' : 'px-4 py-3',
                    col.className,
                  )}
                >
                  {col.sortable
                    ? (
                      <button
                        type='button'
                        onClick={() => changeSort(col.key)}
                        title={t('ui.sortBy', { column: col.label })}
                        className={cn(
                          'inline-flex items-center gap-1 rounded uppercase tracking-wider',
                          'hover:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-plex/60',
                          state !== 'none' && 'text-gray-200',
                        )}
                      >
                        {col.header ?? col.label}
                        <SortIcon
                          size={12}
                          aria-hidden='true'
                          className={state === 'none' ? 'opacity-40' : 'text-plex'}
                        />
                        {state !== 'none' && (
                          <span className='sr-only'>
                            {state === 'ascending' ? t('ui.sortAsc') : t('ui.sortDesc')}
                          </span>
                        )}
                      </button>
                    )
                    : col.header ?? col.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className='divide-y divide-gray-800'>
          {rows.map((row, i) => (
            <tr
              key={rowKey(row, i)}
              className={cn(
                'hover:bg-gray-800/30 transition-colors',
                onRowClick && 'cursor-pointer',
              )}
              onClick={() => onRowClick?.(row)}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={cn(
                    'text-gray-300',
                    compact ? 'px-3 py-2 text-xs' : 'px-4 py-3',
                    col.className,
                  )}
                >
                  {col.render ? col.render(row, i) : String(row[col.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (!searchable) return table;
  return (
    <div className='space-y-3'>
      {searchBox}
      {table}
    </div>
  );
}
